import { localization } from '~/i18n';
import type { DatasetMetadata } from './adapters/types';

/** What names one image of a fused pair: its modality and series number. */
export type PairEnd = Pick<DatasetMetadata, 'modality' | 'seriesNumber'>;

/** A series number as the user reads it, e.g. "Series 3". */
export type SeriesNumberFormatter = (seriesNumber: number) => string;

/**
 * How the two images of a pair are named, row image first, without naming
 * either: the modality code, and the series number too, written by
 * `nameSeries`, when both share a modality, e.g. "CT Series 3". Two images
 * that these still name alike are told apart by their end of the pair, e.g.
 * "CT (row image)" and "CT (secondary image)". None of it is PHI, so all of it
 * is shown in the clear.
 */
export function pairEndLabels(
    rowImage: PairEnd,
    fused: PairEnd,
    nameSeries: SeriesNumberFormatter
): readonly [string, string] {
    const ambiguous = rowImage.modality === fused.modality;
    const labelOf = ({ modality, seriesNumber }: PairEnd): string =>
        ambiguous && seriesNumber != null
            ? `${modality} ${nameSeries(seriesNumber)}`
            : modality;
    const rowLabel = labelOf(rowImage);
    const fusedLabel = labelOf(fused);
    return rowLabel === fusedLabel
        ? [
              localization.t('pairEnd.rowImage', { name: rowLabel }),
              localization.t('pairEnd.secondaryImage', { name: fusedLabel }),
          ]
        : [rowLabel, fusedLabel];
}
